import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  RefreshCw,
  Mail,
  Phone as PhoneIcon,
  GripVertical,
  Users,
  Sparkles,
  Check,
  LayoutGrid,
  BarChart3,
} from "lucide-react";
import CrmStats from "@/components/crm-stats";

// ─── Typy (zgodne z odpowiedzia /api/crm/leads) ──────────────────────────────

type CrmStageDef = { key: string; label: string };

type CrmLeadCard = {
  id: string;
  visitorId: string;
  trackingId: string;
  stage: string;
  stageManual: boolean;
  qualification: "qualified" | "unqualified" | null;
  pipelineValue: string | null;
  lastActivityAt: string | null;
  nextFollowUpAt: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  source: string | null;
  createdAt: string | null;
};

type BoardResponse = { stages: CrmStageDef[]; leads: CrmLeadCard[] };

type CrmSeedMode = "all_new" | "all_auto" | "only_new";
type SettingsResponse = { settings: { seedMode: CrmSeedMode } | null; initialized: boolean };

type Props = { forClientUserId?: string };

// Opcje pierwszego uruchomienia CRM — co zrobic z istniejacymi leadami.
const SEED_OPTIONS: Array<{ mode: CrmSeedMode; title: string; desc: string; icon: typeof Users }> =
  [
    {
      mode: "all_new",
      title: "Wszystkie leady jako Nowy",
      desc: 'Kazdy obecny lead trafia na etap "Nowy". Najprostszy start.',
      icon: Users,
    },
    {
      mode: "all_auto",
      title: "Wszystkie wg etapu sprzedazy",
      desc: "Obecne leady rozkladane automatycznie (rozmowa, sprzedaz, itd.) wg historii konwersji.",
      icon: Sparkles,
    },
    {
      mode: "only_new",
      title: "Tylko nowe leady od teraz",
      desc: "Zaczynasz z czysta tablica — do CRM wpadaja wylacznie leady pozyskane od teraz.",
      icon: Check,
    },
  ];

// Kolor akcentu kolumny po etapie — spojny z paleta Sales Data (#6E3AFF).
const STAGE_ACCENT: Record<string, string> = {
  new: "border-t-white/30",
  contacted: "border-t-sky-400/60",
  booked: "border-t-[#6E3AFF]/70",
  showed: "border-t-indigo-400/70",
  proposal: "border-t-amber-400/70",
  won: "border-t-emerald-400/80",
  lost: "border-t-rose-400/60",
};

function initials(card: CrmLeadCard): string {
  const base = card.name || card.email || card.phone || "?";
  return base.trim().slice(0, 2).toUpperCase();
}

export default function SalesKanban({ forClientUserId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  const [selectedMode, setSelectedMode] = useState<CrmSeedMode>("all_new");
  const [crmView, setCrmView] = useState<"board" | "stats">("board");
  const qs = forClientUserId ? `?forClientUserId=${forClientUserId}` : "";
  const queryKey = ["/api/crm/leads", { forClientUserId }];
  const settingsKey = ["/api/crm/settings", { forClientUserId }];

  const { data: settingsData, isLoading: settingsLoading } = useQuery<SettingsResponse>({
    queryKey: settingsKey,
    queryFn: () => apiRequest("GET", `/api/crm/settings${qs}`),
  });
  const initialized = settingsData?.initialized ?? false;

  const { data, isLoading } = useQuery<BoardResponse>({
    queryKey,
    queryFn: () => apiRequest("GET", `/api/crm/leads${qs}`),
    enabled: initialized, // tablica ma sens dopiero po konfiguracji
  });

  const initMutation = useMutation({
    mutationFn: (mode: CrmSeedMode) =>
      apiRequest<{ created: number }>("POST", `/api/crm/init${qs}`, { mode }),
    onSuccess: (res: { created: number }) => {
      queryClient.invalidateQueries({ queryKey: settingsKey });
      queryClient.invalidateQueries({ queryKey });
      toast({ title: `CRM wlaczony — dodano ${res.created} leadow` });
    },
    onError: () => toast({ title: "Nie udalo sie wlaczyc CRM", variant: "destructive" }),
  });

  // Po inicjalizacji raz dociagnij nowe leady (tryb only_new / leady od ostatniej wizyty).
  const autoSyncedRef = useRef(false);
  useEffect(() => {
    if (initialized && !autoSyncedRef.current) {
      autoSyncedRef.current = true;
      apiRequest<{ created: number }>("POST", `/api/crm/sync${qs}`)
        .then((r) => {
          if (r.created > 0) queryClient.invalidateQueries({ queryKey });
        })
        .catch(() => undefined);
    }
  }, [initialized, qs, queryClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const leadsByStage = useMemo(() => {
    const map = new Map<string, CrmLeadCard[]>();
    for (const stage of data?.stages ?? []) map.set(stage.key, []);
    for (const lead of data?.leads ?? []) {
      if (!map.has(lead.stage)) map.set(lead.stage, []);
      map.get(lead.stage)!.push(lead);
    }
    return map;
  }, [data]);

  const moveMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) =>
      apiRequest(
        "PATCH",
        `/api/crm/leads/${id}${forClientUserId ? `?forClientUserId=${forClientUserId}` : ""}`,
        { stage }
      ),
    onMutate: async ({ id, stage }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BoardResponse>(queryKey);
      if (prev) {
        queryClient.setQueryData<BoardResponse>(queryKey, {
          ...prev,
          leads: prev.leads.map((l) => (l.id === id ? { ...l, stage } : l)),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
      toast({ title: "Nie udalo sie zmienic etapu", variant: "destructive" });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const qualifyMutation = useMutation({
    mutationFn: ({
      id,
      qualification,
    }: {
      id: string;
      qualification: "qualified" | "unqualified" | null;
    }) => apiRequest("PATCH", `/api/crm/leads/${id}${qs}`, { qualification }),
    onMutate: async ({ id, qualification }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BoardResponse>(queryKey);
      if (prev) {
        queryClient.setQueryData<BoardResponse>(queryKey, {
          ...prev,
          leads: prev.leads.map((l) => (l.id === id ? { ...l, qualification } : l)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
      toast({ title: "Nie udalo sie zmienic kwalifikacji", variant: "destructive" });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ created: number }>(
        "POST",
        `/api/crm/sync${forClientUserId ? `?forClientUserId=${forClientUserId}` : ""}`
      ),
    onSuccess: (res: { created: number }) => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: res.created > 0 ? `Dodano ${res.created} leadow do CRM` : "CRM jest aktualny",
      });
    },
    onError: () => toast({ title: "Synchronizacja nie powiodla sie", variant: "destructive" }),
  });

  function handleDrop(stageKey: string) {
    const id = draggedId;
    setDraggedId(null);
    setDragOverStage(null);
    if (!id) return;
    const lead = data?.leads.find((l) => l.id === id);
    if (!lead || lead.stage === stageKey) return;
    moveMutation.mutate({ id, stage: stageKey });
  }

  if (settingsLoading) {
    return <div className="h-64 rounded-xl border border-white/10 bg-white/[0.03] animate-pulse" />;
  }

  // Pierwsze uruchomienie — wybor jak zaseedowac istniejace leady.
  if (!initialized) {
    return (
      <div className="max-w-2xl mx-auto mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <h2 className="text-lg font-semibold text-white">Wlacz CRM dla Sales Data</h2>
        <p className="text-sm text-white/40 mt-1 mb-5">
          Wybierz, co zrobic z leadami ktorych juz masz. Mozesz to potem zsynchronizowac ponownie.
        </p>
        <div className="space-y-2.5">
          {SEED_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = selectedMode === opt.mode;
            return (
              <button
                key={opt.mode}
                onClick={() => setSelectedMode(opt.mode)}
                className={`w-full text-left flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${
                  active
                    ? "border-[#6E3AFF]/70 bg-[#6E3AFF]/10"
                    : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                }`}
              >
                <div
                  className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${active ? "bg-[#6E3AFF]/30 text-[#b79bff]" : "bg-white/5 text-white/50"}`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white/90">{opt.title}</p>
                  <p className="text-xs text-white/40 mt-0.5">{opt.desc}</p>
                </div>
                <div
                  className={`ml-auto w-4 h-4 shrink-0 rounded-full border mt-1 ${active ? "border-[#6E3AFF] bg-[#6E3AFF]" : "border-white/20"}`}
                />
              </button>
            );
          })}
        </div>
        <Button
          onClick={() => initMutation.mutate(selectedMode)}
          disabled={initMutation.isPending}
          className="mt-5 bg-[#6E3AFF] hover:bg-[#5a2ee0] text-white gap-2"
        >
          {initMutation.isPending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
          Wlacz CRM
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
          <button
            onClick={() => setCrmView("board")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              crmView === "board" ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            Tablica
          </button>
          <button
            onClick={() => setCrmView("stats")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              crmView === "stats" ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Statystyki
          </button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          title="Zaciagnij leady z Sales Data do CRM (idempotentne)"
          className="border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${syncMutation.isPending ? "animate-spin" : ""}`} />
          Synchronizuj leady
        </Button>
      </div>

      {crmView === "stats" ? (
        <CrmStats leads={data?.leads ?? []} stages={data?.stages ?? []} />
      ) : isLoading ? (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="w-72 shrink-0 h-64 rounded-xl border border-white/10 bg-white/[0.03] animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {(data?.stages ?? []).map((stage) => {
            const cards = leadsByStage.get(stage.key) ?? [];
            const isOver = dragOverStage === stage.key;
            return (
              <div
                key={stage.key}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverStage(stage.key);
                }}
                onDragLeave={() => setDragOverStage((s) => (s === stage.key ? null : s))}
                onDrop={() => handleDrop(stage.key)}
                className={`w-72 shrink-0 rounded-xl border bg-white/[0.02] border-t-2 ${STAGE_ACCENT[stage.key] ?? "border-t-white/30"} ${
                  isOver ? "border-[#6E3AFF]/60 bg-[#6E3AFF]/5" : "border-white/10"
                } transition-colors`}
              >
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/10">
                  <span className="text-sm font-semibold text-white/90">{stage.label}</span>
                  <span className="text-[11px] text-white/40 bg-white/5 rounded-full px-2 py-0.5">
                    {cards.length}
                  </span>
                </div>

                <div className="p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-340px)] overflow-y-auto">
                  {cards.length === 0 ? (
                    <div className="text-[11px] text-white/20 text-center py-6">Brak leadow</div>
                  ) : (
                    cards.map((card) => (
                      <div
                        key={card.id}
                        draggable
                        onDragStart={() => setDraggedId(card.id)}
                        onDragEnd={() => setDraggedId(null)}
                        className={`group rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] p-2.5 cursor-grab active:cursor-grabbing ${
                          draggedId === card.id ? "opacity-40" : ""
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-7 h-7 shrink-0 rounded-full bg-[#6E3AFF]/20 text-[#b79bff] text-[11px] font-semibold flex items-center justify-center">
                            {initials(card)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white/90 truncate">
                              {card.name || card.email || card.phone || "—"}
                            </p>
                            {card.email && (
                              <p className="text-[11px] text-white/40 truncate flex items-center gap-1">
                                <Mail className="w-3 h-3 shrink-0" />
                                {card.email}
                              </p>
                            )}
                            {card.phone && (
                              <p className="text-[11px] text-white/40 truncate flex items-center gap-1">
                                <PhoneIcon className="w-3 h-3 shrink-0" />
                                {card.phone}
                              </p>
                            )}
                          </div>
                          <GripVertical className="w-3.5 h-3.5 text-white/15 group-hover:text-white/30 shrink-0" />
                        </div>
                        {card.source && (
                          <div className="mt-2">
                            <span className="text-[10px] text-white/50 bg-white/5 rounded px-1.5 py-0.5">
                              {card.source}
                            </span>
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-1">
                          <button
                            onClick={() =>
                              qualifyMutation.mutate({
                                id: card.id,
                                qualification:
                                  card.qualification === "qualified" ? null : "qualified",
                              })
                            }
                            className={`flex-1 text-[10px] rounded px-1.5 py-1 border transition-colors ${
                              card.qualification === "qualified"
                                ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-300"
                                : "border-white/10 text-white/40 hover:text-white/70 hover:border-white/20"
                            }`}
                          >
                            Kwalifikowany
                          </button>
                          <button
                            onClick={() =>
                              qualifyMutation.mutate({
                                id: card.id,
                                qualification:
                                  card.qualification === "unqualified" ? null : "unqualified",
                              })
                            }
                            className={`flex-1 text-[10px] rounded px-1.5 py-1 border transition-colors ${
                              card.qualification === "unqualified"
                                ? "border-rose-400/50 bg-rose-400/15 text-rose-300"
                                : "border-white/10 text-white/40 hover:text-white/70 hover:border-white/20"
                            }`}
                          >
                            Niekwalifikowany
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
